/**
 * Учебная электронная таблица для ЕГЭ 3 / 18.
 * Live-черновик формулы через awareness; режим указания ячеек как в Calc.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import api from '../../api/client';
import { useWorkspaceSync } from './useWorkspaceSync';
import {
  colLabel,
  displayValue,
  makeEvaluator,
  rawCell,
} from './spreadsheet/formulaEngine.js';
import {
  FILL_PRESETS,
  MAX_FULL_COLS,
  applyFillToSelection,
  applyWallsToSelection,
  clearWallsInSelection,
  copyFormat,
  deleteRows,
  emptySpreadsheetState,
  fillFormulas,
  getFill,
  getWalls,
  isInSelection,
  normalizeSpreadsheetState,
  selectionBounds,
  setCellValue,
  shouldVirtualize,
} from './spreadsheet/cellModel.js';
import {
  buildSheetAwarenessPayload,
  clearLocalSheetAwareness,
  listRemoteSheetStates,
  remoteEditorOnCell,
  setLocalSheetAwareness,
} from './spreadsheet/awarenessDraft.js';
import {
  applyPointedRef,
  canPointInsert,
  movePoint,
} from './spreadsheet/formulaPointing.js';

const ROW_H = 28;
const OVERSCAN = 8;
const AWARENESS_THROTTLE_MS = 60;

function isSheetEmpty(state) {
  const cells = state?.cells;
  if (!cells || typeof cells !== 'object') return true;
  return Object.keys(cells).length === 0;
}

function clampSel(sel, rows, cols) {
  if (!sel) return null;
  return {
    r0: Math.max(0, Math.min(rows - 1, sel.r0)),
    r1: Math.max(0, Math.min(rows - 1, sel.r1)),
    c0: Math.max(0, Math.min(cols - 1, sel.c0)),
    c1: Math.max(0, Math.min(cols - 1, sel.c1)),
  };
}

function wallStyle(walls) {
  if (!walls) return undefined;
  const w = '3px solid #e2e8f0';
  const style = {};
  if (walls.t) style.borderTop = w;
  if (walls.r) style.borderRight = w;
  if (walls.b) style.borderBottom = w;
  if (walls.l) style.borderLeft = w;
  return style;
}

export default function SpreadsheetWorkspace({
  ydoc,
  awareness = null,
  workspaceConfig,
  seedState,
  dataKey = 'data',
  onStateChange,
  readOnly = false,
  taskSlug = null,
}) {
  const initial = useMemo(() => {
    const cfg = workspaceConfig && typeof workspaceConfig === 'object' ? workspaceConfig : {};
    let rows = Number(cfg.rows) > 0 ? Number(cfg.rows) : 20;
    let cols = Number(cfg.cols) > 0 ? Number(cfg.cols) : 20;
    rows = Math.min(Math.max(1, rows), 20000);
    cols = Math.min(Math.max(1, cols), MAX_FULL_COLS);
    const base = emptySpreadsheetState(rows, cols);
    return normalizeSpreadsheetState(
      {
        ...base,
        ...(cfg.cells ? { cells: cfg.cells } : {}),
        ...(cfg.fills ? { fills: cfg.fills } : {}),
        ...(cfg.walls ? { walls: cfg.walls } : {}),
        rows,
        cols,
      },
      base,
    );
  }, [workspaceConfig]);

  const [state, setState] = useWorkspaceSync({
    ydoc,
    initialState: initial,
    seedState: seedState ? normalizeSpreadsheetState(seedState, initial) : null,
    dataKey,
    onChange: onStateChange,
  });

  const rows = state.rows || 20;
  const cols = Math.min(state.cols || 20, MAX_FULL_COLS);
  const virtual = shouldVirtualize(state, workspaceConfig);
  const evalCell = useMemo(() => makeEvaluator(state), [state]);

  const [sel, setSel] = useState({ r0: 0, r1: 0, c0: 0, c1: 0 });
  const [active, setActive] = useState({ row: 0, col: 0 });
  /** @type {[null|{row,col,draft,caret?:number}, Function]} */
  const [editing, setEditing] = useState(null);
  const [formulaBar, setFormulaBar] = useState('');
  /** @type {[null|{refStart,refEnd,row,col}, Function]} */
  const [pointing, setPointing] = useState(null);
  const [paintMode, setPaintMode] = useState(false);
  const [fillColor, setFillColor] = useState(FILL_PRESETS[1].color);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetError, setSheetError] = useState('');
  const [sheetSource, setSheetSource] = useState('');
  const [remotes, setRemotes] = useState([]);
  const [lockHint, setLockHint] = useState('');

  const autoLoadTried = useRef(false);
  const stateRef = useRef(state);
  stateRef.current = state;
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const pointingRef = useRef(pointing);
  pointingRef.current = pointing;
  const activeRef = useRef(active);
  activeRef.current = active;
  const selRef = useRef(sel);
  selRef.current = sel;
  const skipBlurCommitRef = useRef(false);
  const awarenessTimerRef = useRef(null);
  const dragging = useRef(null);
  const fillOrigin = useRef(null);
  const scrollRef = useRef(null);
  const editInputRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewH, setViewH] = useState(400);

  const publishAwareness = useCallback((nextEditing, nextPointing, nextActive, nextSel) => {
    if (!awareness) return;
    const ed = nextEditing === undefined ? editingRef.current : nextEditing;
    const pt = nextPointing === undefined ? pointingRef.current : nextPointing;
    const act = nextActive === undefined ? activeRef.current : nextActive;
    const selection = nextSel === undefined ? selRef.current : nextSel;
    const payload = buildSheetAwarenessPayload({
      dataKey,
      active: act,
      sel: selection,
      editing: ed,
      pointTo: pt ? { row: pt.row, col: pt.col } : (ed?.pointTo || null),
    });
    if (awarenessTimerRef.current) clearTimeout(awarenessTimerRef.current);
    awarenessTimerRef.current = setTimeout(() => {
      setLocalSheetAwareness(awareness, payload);
    }, AWARENESS_THROTTLE_MS);
  }, [awareness, dataKey]);

  const flushAwarenessNow = useCallback((nextEditing, nextPointing) => {
    if (!awareness) return;
    if (awarenessTimerRef.current) {
      clearTimeout(awarenessTimerRef.current);
      awarenessTimerRef.current = null;
    }
    const ed = nextEditing === undefined ? editingRef.current : nextEditing;
    const pt = nextPointing === undefined ? pointingRef.current : nextPointing;
    setLocalSheetAwareness(
      awareness,
      buildSheetAwarenessPayload({
        dataKey,
        active: activeRef.current,
        sel: selRef.current,
        editing: ed,
        pointTo: pt ? { row: pt.row, col: pt.col } : null,
      }),
    );
  }, [awareness, dataKey]);

  useEffect(() => {
    if (!awareness) {
      setRemotes([]);
      return undefined;
    }
    const sync = () => setRemotes(listRemoteSheetStates(awareness, dataKey));
    sync();
    awareness.on('change', sync);
    return () => {
      awareness.off('change', sync);
      clearLocalSheetAwareness(awareness);
      if (awarenessTimerRef.current) clearTimeout(awarenessTimerRef.current);
    };
  }, [awareness, dataKey]);

  // Навигация без правки — тоже в awareness (рамка ведущего)
  useEffect(() => {
    if (!awareness || editing) return;
    publishAwareness(null, null, active, sel);
  }, [awareness, active, sel, editing, publishAwareness]);

  const applySheetState = useCallback((raw) => {
    const next = normalizeSpreadsheetState(raw, emptySpreadsheetState(
      Number(raw?.rows) || 20,
      Number(raw?.cols) || 20,
    ));
    setState(next);
    setSheetSource(String(raw?.source || ''));
    setActive({ row: 0, col: 0 });
    setSel({ r0: 0, r1: 0, c0: 0, c1: 0 });
    setEditing(null);
    setPointing(null);
    setFormulaBar('');
    flushAwarenessNow(null, null);
  }, [setState, flushAwarenessNow]);

  const loadFromTaskFile = useCallback(async ({ force = false, onlyIfEmpty = false } = {}) => {
    if (!taskSlug || readOnly) return;
    if (onlyIfEmpty && !isSheetEmpty(stateRef.current)) return;
    setSheetLoading(true);
    setSheetError('');
    try {
      const qs = force ? '?force=1' : '';
      const { data } = await api.get(`/tasks/${encodeURIComponent(taskSlug)}/spreadsheet-sheet/${qs}`);
      if (!data || typeof data !== 'object') throw new Error('Пустой ответ сервера');
      applySheetState(data);
    } catch (err) {
      setSheetError(String(err?.response?.data?.detail || err?.message || 'Не удалось загрузить таблицу'));
    } finally {
      setSheetLoading(false);
    }
  }, [taskSlug, readOnly, applySheetState]);

  useEffect(() => {
    autoLoadTried.current = false;
  }, [taskSlug]);

  useEffect(() => {
    if (!taskSlug || readOnly || autoLoadTried.current) return undefined;
    const t = window.setTimeout(() => {
      if (autoLoadTried.current) return;
      autoLoadTried.current = true;
      void loadFromTaskFile({ onlyIfEmpty: true });
    }, 700);
    return () => window.clearTimeout(t);
  }, [taskSlug, readOnly, loadFromTaskFile]);

  useEffect(() => {
    if (!editing) return;
    const raw = rawCell(state, active.row, active.col);
    // во время правки formulaBar ведём из draft
    void raw;
  }, [state, active, editing]);

  useEffect(() => {
    if (!editing) {
      setFormulaBar(rawCell(state, active.row, active.col));
    }
  }, [state, active, editing]);

  useEffect(() => {
    if (editing && editInputRef.current) {
      const el = editInputRef.current;
      el.focus();
      if (editing.caret != null && typeof el.setSelectionRange === 'function') {
        const c = Math.max(0, Math.min(editing.draft.length, editing.caret));
        el.setSelectionRange(c, c);
      } else {
        el.select();
      }
    }
  // только при старте правки новой клетки
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.row, editing?.col]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onScroll = () => setScrollTop(el.scrollTop);
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => setViewH(el.clientHeight || 400))
      : null;
    el.addEventListener('scroll', onScroll, { passive: true });
    setViewH(el.clientHeight || 400);
    if (ro) ro.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (ro) ro.disconnect();
    };
  }, [virtual]);

  const cancelEdit = useCallback(() => {
    setEditing(null);
    setPointing(null);
    setFormulaBar(rawCell(stateRef.current, activeRef.current.row, activeRef.current.col));
    flushAwarenessNow(null, null);
  }, [flushAwarenessNow]);

  const commitEdit = useCallback(() => {
    if (!editingRef.current || readOnly) {
      setEditing(null);
      setPointing(null);
      flushAwarenessNow(null, null);
      return;
    }
    const ed = editingRef.current;
    setState((prev) => setCellValue(prev, ed.row, ed.col, ed.draft));
    setEditing(null);
    setPointing(null);
    setFormulaBar(ed.draft);
    flushAwarenessNow(null, null);
  }, [readOnly, setState, flushAwarenessNow]);

  const startEdit = useCallback((r, c, initialDraft, opts = {}) => {
    if (readOnly) return false;
    const remote = remoteEditorOnCell(remotes, r, c);
    if (remote && !opts.force) {
      setLockHint(remote.name
        ? `Сейчас правит ${remote.name}`
        : 'Ячейку сейчас правит другой участник');
      window.setTimeout(() => setLockHint(''), 2500);
      return false;
    }
    const raw = initialDraft !== undefined ? initialDraft : rawCell(stateRef.current, r, c);
    const caret = opts.caret != null ? opts.caret : raw.length;
    setActive({ row: r, col: c });
    setSel({ r0: r, r1: r, c0: c, c1: c });
    const ed = { row: r, col: c, draft: raw, caret };
    setEditing(ed);
    setFormulaBar(raw);
    setPointing(null);
    flushAwarenessNow(ed, null);
    return true;
  }, [readOnly, remotes, flushAwarenessNow]);

  const insertPointedCell = useCallback((r, c) => {
    const ed = editingRef.current;
    if (!ed || readOnly) return;
    const caret = ed.caret != null ? ed.caret : ed.draft.length;
    if (!canPointInsert(ed.draft, caret) && !pointingRef.current) return;
    const applied = applyPointedRef(ed.draft, caret, r, c, pointingRef.current);
    setPointing(applied.pointing);
    pointingRef.current = applied.pointing;
    const nextEd = {
      ...ed,
      draft: applied.draft,
      caret: applied.caret,
    };
    setEditing(nextEd);
    editingRef.current = nextEd;
    setFormulaBar(applied.draft);
    flushAwarenessNow(nextEd, applied.pointing);
    requestAnimationFrame(() => {
      const el = editInputRef.current;
      if (el && typeof el.setSelectionRange === 'function') {
        el.focus();
        el.setSelectionRange(applied.caret, applied.caret);
      }
    });
  }, [readOnly, flushAwarenessNow]);

  const selectCell = useCallback((r, c, extend = false) => {
    if (paintMode && !readOnly) {
      setState((prev) => copyFormat(prev, activeRef.current, {
        r0: r, r1: r, c0: c, c1: c,
      }));
      setPaintMode(false);
      setActive({ row: r, col: c });
      setSel({ r0: r, r1: r, c0: c, c1: c });
      return;
    }
    setActive({ row: r, col: c });
    if (extend) {
      setSel((prev) => ({
        r0: prev.r0, c0: prev.c0, r1: r, c1: c,
      }));
    } else {
      setSel({ r0: r, r1: r, c0: c, c1: c });
    }
  }, [paintMode, readOnly, setState]);

  const onCellMouseDown = (e, r, c) => {
    if (e.button !== 0) return;
    e.preventDefault();

    // Режим указания: клик вставляет ссылку, не коммитит
    const ed = editingRef.current;
    if (ed && String(ed.draft || '').startsWith('=')) {
      const caret = ed.caret != null ? ed.caret : ed.draft.length;
      if (canPointInsert(ed.draft, caret) || pointingRef.current) {
        skipBlurCommitRef.current = true;
        insertPointedCell(r, c);
        window.setTimeout(() => { skipBlurCommitRef.current = false; }, 0);
        return;
      }
    }

    if (editingRef.current) commitEdit();
    if (e.shiftKey) selectCell(r, c, true);
    else selectCell(r, c, false);

    dragging.current = 'select';
    const onMove = (ev) => {
      const t = document.elementFromPoint(ev.clientX, ev.clientY);
      const cell = t?.closest?.('[data-ss-r]');
      if (!cell) return;
      const nr = Number(cell.getAttribute('data-ss-r'));
      const nc = Number(cell.getAttribute('data-ss-c'));
      if (Number.isFinite(nr) && Number.isFinite(nc)) {
        setSel((prev) => ({ ...prev, r1: nr, c1: nc }));
        setActive({ row: nr, col: nc });
      }
    };
    const onUp = () => {
      dragging.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onFillHandleMouseDown = (e) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    const bounds = selectionBounds(sel);
    if (!bounds) return;
    dragging.current = 'fill';
    const origin = {
      row: active.row,
      col: active.col,
      sel: { ...bounds },
      dest: { ...bounds },
    };
    fillOrigin.current = origin;
    const onMove = (ev) => {
      const t = document.elementFromPoint(ev.clientX, ev.clientY);
      const cell = t?.closest?.('[data-ss-r]');
      if (!cell) return;
      const nr = Number(cell.getAttribute('data-ss-r'));
      const nc = Number(cell.getAttribute('data-ss-c'));
      if (!Number.isFinite(nr) || !Number.isFinite(nc)) return;
      const next = {
        r0: origin.sel.r0, c0: origin.sel.c0, r1: nr, c1: nc,
      };
      origin.dest = next;
      setSel(next);
    };
    const onUp = () => {
      dragging.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const src = fillOrigin.current;
      fillOrigin.current = null;
      if (!src?.dest) return;
      const dest = selectionBounds(src.dest);
      if (!dest) return;
      setState((prev) => fillFormulas(prev, { row: src.row, col: src.col }, dest));
      setSel(src.dest);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onEditBlur = () => {
    if (skipBlurCommitRef.current) {
      skipBlurCommitRef.current = false;
      return;
    }
    commitEdit();
  };

  const onKeyDown = (e) => {
    if (editingRef.current) {
      const ed = editingRef.current;
      const caret = (editInputRef.current && typeof editInputRef.current.selectionStart === 'number')
        ? editInputRef.current.selectionStart
        : (ed.caret ?? ed.draft.length);

      if (e.key === 'Enter') {
        e.preventDefault();
        commitEdit();
        const nr = Math.min(rows - 1, ed.row + 1);
        setActive({ row: nr, col: ed.col });
        setSel({ r0: nr, r1: nr, c0: ed.col, c1: ed.col });
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelEdit();
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        commitEdit();
        const nc = Math.min(cols - 1, ed.col + (e.shiftKey ? -1 : 1));
        setActive({ row: ed.row, col: Math.max(0, nc) });
        setSel({
          r0: ed.row, r1: ed.row, c0: Math.max(0, nc), c1: Math.max(0, nc),
        });
        return;
      }

      // Указание ячейки стрелками
      const arrowMap = {
        ArrowUp: [-1, 0],
        ArrowDown: [1, 0],
        ArrowLeft: [0, -1],
        ArrowRight: [0, 1],
      };
      if (arrowMap[e.key] && String(ed.draft).startsWith('=')) {
        if (canPointInsert(ed.draft, caret) || pointingRef.current) {
          e.preventDefault();
          e.stopPropagation();
          const [dr, dc] = arrowMap[e.key];
          let baseR;
          let baseC;
          if (pointingRef.current) {
            baseR = pointingRef.current.row;
            baseC = pointingRef.current.col;
          } else {
            baseR = ed.row;
            baseC = ed.col;
          }
          const next = movePoint(baseR, baseC, dr, dc, rows, cols);
          insertPointedCell(next.row, next.col);
          return;
        }
      }

      // обычный ввод — сброс pointing, если печатаем не стрелкой
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        if (pointingRef.current) {
          setPointing(null);
          pointingRef.current = null;
        }
      }
      return;
    }

    if (e.key === 'F2' || e.key === 'Enter') {
      e.preventDefault();
      startEdit(active.row, active.col);
      return;
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (readOnly) return;
      e.preventDefault();
      const bounds = selectionBounds(sel);
      if (!bounds) return;
      setState((prev) => {
        let next = prev;
        for (let r = bounds.r0; r <= bounds.r1; r += 1) {
          for (let c = bounds.c0; c <= bounds.c1; c += 1) {
            next = setCellValue(next, r, c, '');
          }
        }
        return next;
      });
      setFormulaBar('');
      return;
    }

    const move = (dr, dc, extend) => {
      e.preventDefault();
      const nr = Math.max(0, Math.min(rows - 1, active.row + dr));
      const nc = Math.max(0, Math.min(cols - 1, active.col + dc));
      selectCell(nr, nc, extend);
    };

    if (e.key === 'ArrowUp') move(-1, 0, e.shiftKey);
    else if (e.key === 'ArrowDown') move(1, 0, e.shiftKey);
    else if (e.key === 'ArrowLeft') move(0, -1, e.shiftKey);
    else if (e.key === 'ArrowRight') move(0, 1, e.shiftKey);
    else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      startEdit(active.row, active.col, e.key, { caret: 1 });
    }
  };

  const onEditInputChange = (e) => {
    const draft = e.target.value;
    const caret = e.target.selectionStart ?? draft.length;
    if (pointingRef.current) {
      setPointing(null);
      pointingRef.current = null;
    }
    const nextEd = editingRef.current
      ? { ...editingRef.current, draft, caret }
      : null;
    setEditing(nextEd);
    editingRef.current = nextEd;
    setFormulaBar(draft);
    publishAwareness(nextEd, null);
  };

  const onEditSelect = (e) => {
    if (!editingRef.current) return;
    const caret = e.target.selectionStart ?? editingRef.current.draft.length;
    const nextEd = { ...editingRef.current, caret };
    setEditing(nextEd);
    editingRef.current = nextEd;
    publishAwareness(nextEd, pointingRef.current);
  };

  const b = selectionBounds(clampSel(sel, rows, cols));

  let rowStart = 0;
  let rowEnd = rows - 1;
  let topPad = 0;
  let bottomPad = 0;
  if (virtual) {
    const first = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN);
    const visible = Math.ceil(viewH / ROW_H) + OVERSCAN * 2;
    rowStart = first;
    rowEnd = Math.min(rows - 1, first + visible);
    topPad = rowStart * ROW_H;
    bottomPad = Math.max(0, (rows - 1 - rowEnd) * ROW_H);
  }

  // Удалённые указатели / черновики
  const remotePointHighlights = [];
  remotes.forEach((r) => {
    const pt = r.sheet?.editing?.pointTo;
    if (pt) remotePointHighlights.push({ ...pt, color: r.color, clientId: r.clientId });
    const act = r.sheet?.active;
    if (act && !r.sheet?.editing) {
      remotePointHighlights.push({
        row: act.row, col: act.col, color: r.color, clientId: r.clientId, soft: true,
      });
    }
  });

  const localPoint = pointing;

  const renderCell = (r, c) => {
    const raw = rawCell(state, r, c);
    const shown = displayValue(state, r, c, evalCell);
    const isFormula = raw.startsWith('=');
    const isActive = active.row === r && active.col === c;
    const selected = isInSelection(sel, r, c);
    const isEdit = editing && editing.row === r && editing.col === c;
    const fill = getFill(state, r, c);
    const walls = getWalls(state, r, c);
    const isFillHandle = b && r === b.r1 && c === b.c1 && isActive && !editing;
    const remoteEd = remoteEditorOnCell(remotes, r, c);
    const isLocalPoint = localPoint && localPoint.row === r && localPoint.col === c;
    const remotePoint = remotePointHighlights.find((p) => p.row === r && p.col === c && !p.soft);
    const remoteSoft = remotePointHighlights.find((p) => p.row === r && p.col === c && p.soft);

    let displayText = isFormula ? shown : raw;
    if (remoteEd && !isEdit) {
      displayText = remoteEd.sheet.editing.draft;
    }

    return (
      <td
        key={c}
        data-ss-r={r}
        data-ss-c={c}
        className={[
          'ss-td',
          isFormula ? 'is-formula' : '',
          selected ? 'is-selected' : '',
          isActive ? 'is-active' : '',
          remoteEd ? 'is-remote-edit' : '',
          isLocalPoint ? 'is-point-target' : '',
          remotePoint ? 'is-remote-point' : '',
        ].filter(Boolean).join(' ')}
        style={{
          background: fill || undefined,
          ...wallStyle(walls),
          height: ROW_H,
          ...(remoteEd ? { outlineColor: remoteEd.color, boxShadow: `inset 0 0 0 2px ${remoteEd.color}` } : null),
          ...(isLocalPoint ? { boxShadow: 'inset 0 0 0 2px #fbbf24' } : null),
          ...(remotePoint ? { boxShadow: `inset 0 0 0 2px ${remotePoint.color}` } : null),
          ...(remoteSoft && !remoteEd && !isLocalPoint && !remotePoint
            ? { outline: `1px dashed ${remoteSoft.color}`, outlineOffset: -1 }
            : null),
        }}
        onMouseDown={(ev) => onCellMouseDown(ev, r, c)}
        onDoubleClick={() => startEdit(r, c)}
      >
        {isEdit ? (
          <input
            ref={editInputRef}
            className="form-input ss-cell ss-cell-edit"
            value={editing.draft}
            onChange={onEditInputChange}
            onSelect={onEditSelect}
            onClick={onEditSelect}
            onBlur={onEditBlur}
            onKeyDown={onKeyDown}
            aria-label={`${colLabel(c)}${r + 1}`}
          />
        ) : (
          <div
            className={`ss-cell-display ${remoteEd ? 'is-live-draft' : ''}`}
            title={remoteEd ? `Набор: ${remoteEd.sheet.editing.draft}` : (isFormula ? raw : undefined)}
            style={remoteEd ? { color: remoteEd.color } : undefined}
          >
            {remoteEd ? remoteEd.sheet.editing.draft : displayText}
          </div>
        )}
        {isFillHandle && !readOnly ? (
          <span
            className="ss-fill-handle"
            onMouseDown={onFillHandleMouseDown}
            title="Протянуть формулы"
          />
        ) : null}
      </td>
    );
  };

  const formulaBarValue = editing
    ? editing.draft
    : (remoteEditorOnCell(remotes, active.row, active.col)?.sheet?.editing?.draft ?? formulaBar);

  const colHeaders = (
    <tr>
      <th className="ss-corner" />
      {Array.from({ length: cols }, (_, c) => (
        <th key={c} className="ss-col-h">{colLabel(c)}</th>
      ))}
    </tr>
  );

  return (
    <div
      className={`spreadsheet-workspace ${virtual ? 'is-virtual' : ''}`}
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      <div className="ss-toolbar gtw-toolbar">
        <label className="ss-formula-bar">
          <span className="ss-ref">{colLabel(active.col)}{active.row + 1}</span>
          <input
            className="form-input"
            value={formulaBarValue}
            disabled={readOnly}
            onChange={(e) => {
              const v = e.target.value;
              const caret = e.target.selectionStart ?? v.length;
              if (!editing) {
                startEdit(active.row, active.col, v, { caret });
                return;
              }
              if (pointingRef.current) {
                setPointing(null);
                pointingRef.current = null;
              }
              const nextEd = { ...editingRef.current, draft: v, caret };
              setEditing(nextEd);
              editingRef.current = nextEd;
              setFormulaBar(v);
              publishAwareness(nextEd, null);
            }}
            onFocus={() => {
              if (!editing && !readOnly) {
                startEdit(active.row, active.col);
              }
            }}
            onBlur={() => {
              if (skipBlurCommitRef.current) return;
              if (editingRef.current) commitEdit();
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                commitEdit();
                e.currentTarget.blur();
                return;
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                cancelEdit();
                e.currentTarget.blur();
                return;
              }
              // pointing из строки формул
              const ed = editingRef.current;
              if (!ed) return;
              const caret = e.currentTarget.selectionStart ?? ed.draft.length;
              const arrowMap = {
                ArrowUp: [-1, 0],
                ArrowDown: [1, 0],
                ArrowLeft: [0, -1],
                ArrowRight: [0, 1],
              };
              if (arrowMap[e.key] && String(ed.draft).startsWith('=')) {
                if (canPointInsert(ed.draft, caret) || pointingRef.current) {
                  e.preventDefault();
                  const [dr, dc] = arrowMap[e.key];
                  const base = pointingRef.current || { row: ed.row, col: ed.col };
                  const next = movePoint(base.row, base.col, dr, dc, rows, cols);
                  insertPointedCell(next.row, next.col);
                }
              }
            }}
            placeholder="=СУММ(A1:A5)"
            aria-label="Строка формул"
          />
        </label>
        <select
          className="form-input ss-fill-select"
          value={fillColor || ''}
          disabled={readOnly}
          onChange={(e) => setFillColor(e.target.value || null)}
          aria-label="Цвет заливки"
        >
          {FILL_PRESETS.map((p) => (
            <option key={p.id} value={p.color || ''}>{p.label}</option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn-sm"
          disabled={readOnly || !b}
          onClick={() => setState((prev) => applyFillToSelection(prev, b, fillColor))}
        >
          Заливка
        </button>
        <button
          type="button"
          className="btn btn-sm"
          disabled={readOnly || !b}
          onClick={() => setState((prev) => applyWallsToSelection(prev, b))}
          title="Толстые границы по периметру выделения"
        >
          Стены
        </button>
        <button
          type="button"
          className="btn btn-sm"
          disabled={readOnly || !b}
          onClick={() => setState((prev) => clearWallsInSelection(prev, b))}
        >
          Снять стены
        </button>
        <button
          type="button"
          className={`btn btn-sm ${paintMode ? 'is-active-tool' : ''}`}
          disabled={readOnly}
          onClick={() => setPaintMode((v) => !v)}
          title="Скопировать формат активной ячейки на следующую"
        >
          Формат
        </button>
        <button
          type="button"
          className="btn btn-sm"
          disabled={readOnly || !b}
          onClick={() => {
            if (readOnly) return;
            const bounds = selectionBounds(sel);
            if (!bounds) return;
            setState((prev) => deleteRows(prev, bounds.r0, bounds.r1));
            setActive({ row: Math.max(0, bounds.r0), col: bounds.c0 });
            setSel({
              r0: Math.max(0, bounds.r0),
              r1: Math.max(0, bounds.r0),
              c0: bounds.c0,
              c1: bounds.c0,
            });
          }}
          title="Удалить выделенные строки"
        >
          Удалить строки
        </button>
        {taskSlug ? (
          <button
            type="button"
            className="btn btn-sm"
            disabled={readOnly || sheetLoading}
            onClick={() => {
              if (!isSheetEmpty(stateRef.current)
                && !window.confirm('Заменить текущий лист данными из файла задачи?')) {
                return;
              }
              void loadFromTaskFile({ force: false });
            }}
            title="Загрузить .xlsx / .ods из вложений задачи"
          >
            {sheetLoading ? 'Загрузка…' : 'Из файла'}
          </button>
        ) : null}
      </div>
      <p className="ss-hint">
        {virtual
          ? `Лист ${rows}×${cols}, видно окно строк. `
          : `Лист ${rows}×${cols}. `}
        {sheetSource ? `Файл: ${sheetSource}. ` : null}
        {lockHint ? <span className="ss-sheet-error">{lockHint} </span> : null}
        {sheetError ? <span className="ss-sheet-error">{sheetError} </span> : null}
        F2 / «=» — формула; в формуле стрелки или клик указывают ячейку.
        Enter — записать, Esc — отмена.
      </p>
      <div className="ss-scroll" ref={scrollRef}>
        <table className="ss-table">
          <thead>{colHeaders}</thead>
          <tbody>
            {virtual && topPad > 0 ? (
              <tr aria-hidden className="ss-pad-row">
                <td colSpan={cols + 1} style={{ height: topPad, padding: 0, border: 'none' }} />
              </tr>
            ) : null}
            {Array.from({ length: rowEnd - rowStart + 1 }, (_, i) => {
              const r = rowStart + i;
              return (
                <tr key={r} style={{ height: ROW_H }}>
                  <th className="ss-row-h">{r + 1}</th>
                  {Array.from({ length: cols }, (_, c) => renderCell(r, c))}
                </tr>
              );
            })}
            {virtual && bottomPad > 0 ? (
              <tr aria-hidden className="ss-pad-row">
                <td colSpan={cols + 1} style={{ height: bottomPad, padding: 0, border: 'none' }} />
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
